/**
 * Resolves a junction field's stored target ids (link/tagging/attachment) to
 * their entities in one bulk query. Writes don't live here: every owned-row
 * write goes through `@auger/core` (`auger.records.update`), whose planner
 * reconciles the rows.
 */

import type { ApolloClient } from "@apollo/client/core"
import { type Field, getFieldType, isReferenceDef } from "@auger/core/fields"
import { fetchAssetsWhere, fetchRecordsByIds, fetchTagsByIds } from "@auger/core/gql"

type Client = ApolloClient<unknown>
type Target = { id: number | string; [k: string]: unknown }

const RESOLVERS: Partial<Record<string, (client: Client, ids: number[]) => Promise<Target[]>>> = {
  links: (client, ids) =>
    client.query({ query: fetchRecordsByIds, variables: { ids } }).then(({ data }) => data.records),
  taggings: (client, ids) =>
    client.query({ query: fetchTagsByIds, variables: { ids } }).then(({ data }) => data.tags),
  attachments: (client, ids) =>
    client
      .query({ query: fetchAssetsWhere, variables: { where: { id: { _in: ids } } } })
      .then(({ data }) => data.assets),
}

/** Stored target ids → their entities, in one bulk query. Throws when the
 *  field's relation has no target table — junctions only. */
export async function resolveTargets(
  field: Pick<Field, "key" | "type">,
  ids: number[],
  { client }: { client: Client }
): Promise<Target[]> {
  if (!ids.length) return []
  const def = getFieldType(field.type)
  const resolve = isReferenceDef(def) && def.relationship ? RESOLVERS[def.relationship] : undefined
  if (!resolve) throw new Error(`resolveTargets: "${field.key}" has no resolve query`)
  return resolve(client, ids.map(Number))
}
